import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { AuthDialog } from '@/components/auth/AuthDialog';
import { Crown, Star, Save, Download, Zap, Shield } from 'lucide-react';

export const PremiumUpgrade: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    if (!user) {
      toast({
        title: "Please sign in",
        description: "You need to sign in to upgrade to premium",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-payment', {
        body: {
          priceAmount: 500, // $5.00 in cents
          productName: 'Premium Calculator Access',
        },
      });

      if (error) throw error;

      // Open Stripe checkout in a new tab with security hardening
      const newWindow = window.open(data.url, '_blank', 'noopener');
      if (newWindow) newWindow.opener = null;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to start checkout process",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const premiumFeatures = [
    {
      icon: <Save className="w-5 h-5" />,
      title: "Save Calculations",
      description: "Save unlimited calculations with custom names"
    },
    {
      icon: <Download className="w-5 h-5" />,
      title: "Export Results",
      description: "Export calculations to CSV, PDF, or share via link"
    },
    {
      icon: <Shield className="w-5 h-5" />,
      title: "Ad-Free Experience",
      description: "Remove all advertisements for distraction-free calculating"
    },
    {
      icon: <Zap className="w-5 h-5" />,
      title: "Advanced Features",
      description: "Access to premium calculators and advanced functions"
    }
  ];

  return (
    <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
      <CardHeader className="text-center">
        <div className="flex items-center justify-center mb-2">
          <Crown className="w-8 h-8 text-primary mr-2" />
          <CardTitle className="text-2xl">Go Premium</CardTitle>
        </div>
        <CardDescription className="text-lg">
          Unlock powerful features for just <span className="font-bold text-primary">$5.00</span>
        </CardDescription>
        <Badge variant="secondary" className="mx-auto w-fit mt-2">
          <Star className="w-4 h-4 mr-1" />
          One-time payment
        </Badge>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <div className="grid gap-4">
          {premiumFeatures.map((feature, index) => (
            <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-card/50 border border-border/50">
              <div className="text-primary mt-0.5">
                {feature.icon}
              </div>
              <div>
                <h4 className="font-semibold text-sm">{feature.title}</h4>
                <p className="text-xs text-muted-foreground">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>

        {user ? (
          <Button 
            onClick={handleUpgrade} 
            disabled={loading}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
            size="lg"
          >
            {loading ? "Starting checkout..." : "Upgrade to Premium"}
          </Button>
        ) : (
          <AuthDialog>
            <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" size="lg">
              Sign In to Upgrade
            </Button>
          </AuthDialog>
        )}
        
        <p className="text-xs text-center text-muted-foreground">
          Secure payment powered by Stripe. Cancel anytime.
        </p>
      </CardContent>
    </Card>
  );
};